import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import contractService from '../services/contractService';
import ContractStatusBadge from '../components/ContractStatusBadge';

const Contracts = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadContracts();
  }, [page, rowsPerPage, statusFilter, typeFilter, searchTerm]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const response = await contractService.getContracts({
        page: page + 1,
        per_page: rowsPerPage,
        status: statusFilter,
        type: typeFilter,
        search: searchTerm
      });

      setContracts(response.data.contracts);
      setTotalCount(response.data.total);
      setError(null);
    } catch (err) {
      setError('Failed to load contracts');
      console.error('Error loading contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadPDF = async (contractId) => {
    try {
      const response = await contractService.downloadContractPDF(contractId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const handleDelete = async (contractId) => {
    if (window.confirm('Are you sure you want to cancel this contract?')) {
      try {
        await contractService.deleteContract(contractId);
        loadContracts();
      } catch (err) {
        console.error('Error cancelling contract:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contracts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/contracts/new')}
        >
          Create Contract
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, signer..."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="signed">Signed</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="estate_sale">Estate Sale</MenuItem>
              <MenuItem value="consignment">Consignment</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Signer</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Signed</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : contracts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No contracts found
                </TableCell>
              </TableRow>
            ) : (
              contracts.map((contract) => (
                <TableRow key={contract.id} hover>
                  <TableCell>{contract.id}</TableCell>
                  <TableCell>{contract.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={contract.contract_type.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <ContractStatusBadge status={contract.status} size="small" />
                  </TableCell>
                  <TableCell>
                    {contract.signer_name || 'Not sent'}
                    {contract.signer_email && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {contract.signer_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(contract.created_at)}</TableCell>
                  <TableCell>{formatDate(contract.signed_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                      title="View Details"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {contract.status === 'signed' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadPDF(contract.id)}
                        title="Download PDF"
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                    {contract.status !== 'signed' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(contract.id)}
                        title="Cancel Contract"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
    </Container>
  );
};

export default Contracts;
